import rules from '../config/haircutRules.json';

const normalizeFaceShape = (faceShape) => {
  const value = String(faceShape || '').trim().toLowerCase();
  return value || 'unknown';
};

const normalizeHairProfile = (hairProfile) => {
  const src = hairProfile && typeof hairProfile === 'object' ? hairProfile : {};
  return {
    curlType: String(src.curlType || 'unknown').trim().toLowerCase(),
    density: String(src.density || 'unknown').trim().toLowerCase(),
    strandThickness: String(src.strandThickness || 'unknown').trim().toLowerCase(),
    currentLength: String(src.currentLength || 'unknown').trim().toLowerCase(),
    hairline: String(src.hairline || 'unknown').trim().toLowerCase(),
    visibility: String(src.visibility || 'unknown').trim().toLowerCase(),
  };
};

export function normalizeCurlFamily(curlType) {
  if (!curlType) return 'unknown';
  const val = String(curlType).trim().toLowerCase();
  if (!val) return 'unknown';
  if (val.startsWith('1')) return 'straight';
  if (val.startsWith('2')) return 'wavy';
  if (val.startsWith('3')) return 'curly';
  if (val.startsWith('4')) return 'coily';
  return val;
}

const defaults = () => ({ ...(rules.defaults || {}) });
const RECOMMENDATION_LIMIT = 4;

const applyRuleConfig = (out, ruleConfig) => {
  if (!ruleConfig || typeof ruleConfig !== 'object') return;

  const setValues = ruleConfig.set && typeof ruleConfig.set === 'object' ? ruleConfig.set : {};
  Object.entries(setValues).forEach(([key, value]) => {
    out[key] = value;
  });

  Object.entries(ruleConfig).forEach(([key, value]) => {
    if (!Array.isArray(value) || value.length === 0) return;
    if (!key.endsWith('Priority')) return;
    const targetField = key.replace(/Priority$/, '');
    out[targetField] = value[0];
  });
};

export const buildHaircutStructure = ({ faceShape, hairProfile }) => {
  const shape = normalizeFaceShape(faceShape);
  const hair = normalizeHairProfile(hairProfile);
  const out = defaults();
  const faceRule = (rules.faceShapeRules || {})[shape];
  const hairTypeRule = (rules.hairTypeRules || {})[hair.curlType];
  const densityRule = (rules.densityRules || {})[hair.density];
  const hairlineRule = (rules.hairlineRules || {})[hair.hairline];
  const lengthRule = (rules.lengthRules || {})[hair.currentLength];

  [hairTypeRule, faceRule, densityRule, hairlineRule, lengthRule].forEach((rule) => applyRuleConfig(out, rule));

  return out;
};

export function isStyleCompatible(style, profile) {
  const { compatibility = {}, exclusions = {} } = style || {};

  // Hard filter
  for (const key in compatibility) {
    const allowed = compatibility[key];
    const value = profile[key] || profile[String(key || '').toLowerCase()];
    if (Array.isArray(allowed) && !allowed.includes(value)) {
      return false;
    }
  }

  // Exclusions
  for (const key in exclusions) {
    const blocked = exclusions[key];
    const value = profile[key];
    if (Array.isArray(blocked) && blocked.includes(value)) {
      return false;
    }
  }

  return true;
}

export function scoreStyle(style, profile, config) {
  const weights = config.dimensionWeights || {};
  const rankingWeights = config.rankingWeights || {};
  const optionalBoostWeight = config.optionalBoostWeight || 0;
  let score = 0;

  Object.entries(style.compatibility || {}).forEach(([key, allowed]) => {
    if (Array.isArray(allowed) && allowed.includes(profile[key])) {
      score += weights[key] || 0;
    }
  });

  Object.entries(style.optionalBoostTraits || {}).forEach(([key, allowed]) => {
    if (Array.isArray(allowed) && allowed.includes(profile[key])) {
      score += optionalBoostWeight;
    }
  });

  // Specialization boost
  if (style.compatibility?.curlType?.length === 1 || style.compatibility?.curlFamily?.length === 1) {
    score += 15;
  }

  // Generic penalty
  if (style.compatibility?.curlType?.length > 2 || style.compatibility?.curlFamily?.length > 2) {
    score -= 10;
  }

  const ranking = style.ranking || {};
  if (ranking.priority) {
    score += (rankingWeights.priorityBase || 0) * (5 - ranking.priority);
  }
  if (ranking.trending) score += rankingWeights.trending || 0;
  if (ranking.barberRecommended) score += rankingWeights.barberRecommended || 0;
  if (ranking.premium) score += rankingWeights.premium || 0;
  if (ranking.editorial) score += rankingWeights.editorial || 0;

  return score;
}

function matchesExclusions(style, profile) {
  const exclusions = style?.exclusions || {};
  return !Object.entries(exclusions).some(([key, blocked]) => (
    Array.isArray(blocked) && blocked.includes(profile[key])
  ));
}

function matchesCompatibilityField(style, profile, key) {
  const allowed = style?.compatibility?.[key];
  if (!Array.isArray(allowed) || allowed.length === 0) return true;
  return allowed.includes(profile[key]);
}

function isFallbackCompatible(style, profile) {
  if (!matchesExclusions(style, profile)) return false;

  const curlMatch = matchesCompatibilityField(style, profile, 'curlType')
    && matchesCompatibilityField(style, profile, 'curlFamily');
  if (!curlMatch) return false;

  const faceMatch = Array.isArray(style?.compatibility?.faceShape)
    && style.compatibility.faceShape.includes(profile.faceShape);
  const hairMatch = (
    (Array.isArray(style?.compatibility?.curlType) && style.compatibility.curlType.includes(profile.curlType))
    || (Array.isArray(style?.compatibility?.curlFamily) && style.compatibility.curlFamily.includes(profile.curlFamily))
  );

  return faceMatch || hairMatch;
}

function dedupeByFamily(styles) {
  const seen = new Set();
  return styles.filter((style) => {
    const key = String(style.family || style.name || '').trim().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function getRecommendedStyles({ faceShape, hairProfile }) {
  const config = rules.recommendationEngine || {};
  const styles = Array.isArray(config.styles) ? config.styles : [];
  const normalizedHair = normalizeHairProfile(hairProfile);
  const profile = {
    faceShape: normalizeFaceShape(faceShape),
    ...normalizedHair,
    curlType: normalizeCurlFamily(normalizedHair.curlType),
    curlFamily: normalizeCurlFamily(normalizedHair.curlType),
  };

  // Filter out technical entries if they still exist in config.
  const techniqueNames = new Set(['Skin Fade', 'Taper Fade', 'Low Fade', 'Mid Fade']);
  const candidateStyles = styles.filter((style) => !techniqueNames.has(style?.name));

  const compatible = candidateStyles.filter((style) => isStyleCompatible(style, profile));
  const scored = compatible.map((style) => ({
    ...style,
    score: scoreStyle(style, profile, config),
  }));
  scored.sort((a, b) => b.score - a.score);
  const result = dedupeByFamily(scored).slice(0, RECOMMENDATION_LIMIT);

  if (result.length < RECOMMENDATION_LIMIT) {
    const fallbackPool = candidateStyles
      .filter((style) => !result.some((item) => item.name === style.name))
      .filter((style) => isFallbackCompatible(style, profile))
      .map((style) => ({
        ...style,
        score: scoreStyle(style, profile, config),
      }))
      .sort((a, b) => b.score - a.score);

    dedupeByFamily(fallbackPool).forEach((style) => {
      if (result.length < RECOMMENDATION_LIMIT) {
        result.push(style);
      }
    });
  }

  if (result.length > 0) {
    result[0].isTopPick = true;
  }

  return result.slice(0, RECOMMENDATION_LIMIT).map((style) => ({
    ...style,
    structure: buildHaircutStructure({ faceShape, hairProfile }),
  }));
}

export const getHaircutRecommendations = ({ faceShape, hairProfile }) => {
  const structured = getRecommendedStyles({ faceShape, hairProfile });
  return {
    recommendedStyles: structured.map((style) => style.name),
    avoidStyles: [],
    topPick: structured[0]?.name || null,
    structuredRecommendations: structured,
  };
};

const haircutBuilderService = {
  buildHaircutStructure,
  normalizeCurlFamily,
  isStyleCompatible,
  scoreStyle,
  getRecommendedStyles,
  getHaircutRecommendations,
};

export default haircutBuilderService;

